import React, { useContext, useState } from 'react';
import { KatDropdown } from '@amzn/katal-react';
import { useLocalizationContext } from '@amzn/react-arb-tools';
import styled from 'styled-components';
import { logger } from 'src/logger';
import {
  getLocalStorageItem,
  LocalStorageKeys,
  setLocalStorageItem,
} from 'src/utilities/LocalStorage';
import {
  DEFAULT_LOCALE,
  ILocaleAttribute,
} from 'src/constants/LocalizationConstants';
import { LocalizationContextBuilder } from '@amzn/arb-tools';
import { AuthContext } from 'src/state/providers/AuthProvider';
import { CONFIG_TYPE } from 'src/constants/GlobalConstants';
import { fetchFromAppConfig } from 'src/config-store/ConfigFetcher';

export const LanguagePickerContainer = styled.div`
  width: 10rem;
`;

export function sortLanguages(
  locales: Array<ILocaleAttribute> | undefined,
): Array<ILocaleAttribute> | undefined {
  if (!locales) {
    return locales;
  }
  return locales.sort(
    (firstLocale: ILocaleAttribute, secondLocale: ILocaleAttribute) =>
      firstLocale.name.localeCompare(secondLocale.name),
  );
}

export default function LanguagePicker() {
  const language = getLocalStorageItem(LocalStorageKeys.CPR_LANGUAGE);
  const [selectedLanguage, setSelectedLanguage] = useState(
    language || DEFAULT_LOCALE,
  );
  const { preferredAccountId, clientId } = useContext(AuthContext);

  const programConfig = fetchFromAppConfig(
    preferredAccountId,
    clientId,
    CONFIG_TYPE.LOCALIZATION,
  );

  const isLocalizationEnabled: boolean = programConfig?.localizationEnabled;

  if (!isLocalizationEnabled) {
    return <></>;
  }

  // TBD: Set language in userPreferences through API/UMS. Getting from LocalStorage as of now
  const { setLocalizationContext } = useLocalizationContext();
  // TODO: Optimize handleLanguageChange function by using useCallback hook
  const handleLanguageChange = (e: CustomEvent<{ value: string }>) => {
    const language = e.detail.value;
    setSelectedLanguage(language);

    logger.info(`Language Changed to ${language}`);

    // TODO : Set language in userPreferences through API/UMS.
    setLocalStorageItem(LocalStorageKeys.CPR_LANGUAGE, language);
    setLocalizationContext(
      new LocalizationContextBuilder()
        .withLocale(language)
        .withDefaultLocale(DEFAULT_LOCALE)
        .build(),
    );
  };

  const locales = sortLanguages(programConfig?.supportedLocales);

  return (
    <LanguagePickerContainer>
      <KatDropdown
        data-testid="LanguagePicker"
        size="small"
        options={locales}
        onChange={handleLanguageChange}
        value={selectedLanguage}
      />
    </LanguagePickerContainer>
  );
}
